import { useEffect, useState } from 'react';
import { Link, useLocation } from 'wouter';

import { requestJson, responseRecord } from '../../api/http';
import { useSession } from '../../session';

export interface BillingModeBannerContentProps {
  billingTestMode: boolean;
  currentPath: string;
}

/** Pure presentation used by the shell after the authenticated mode read. */
export function BillingModeBannerContent({
  billingTestMode,
  currentPath,
}: BillingModeBannerContentProps) {
  if (!billingTestMode) return null;
  const params = new URLSearchParams();
  params.set('source_path', currentPath);
  params.set('billing_test_mode', String(billingTestMode));

  return (
    <aside className="billing-mode-banner" aria-label="Stripe test mode" data-mode="test" role="status">
      <p>
        Stripe test mode is active. Use test card <strong>4242 4242 4242 4242</strong>.
      </p>
      <p>Any future expiry date and any CVC.</p>
      <Link className="secondary" to={'/support?' + params.toString()}>
        Report a bug
      </Link>
    </aside>
  );
}

export function BillingModeBanner() {
  const { session } = useSession();
  const [location] = useLocation();
  const [billingTestMode, setBillingTestMode] = useState<boolean | null>(null);

  useEffect(() => {
    let active = true;
    setBillingTestMode(null);
    if (!session) return () => { active = false; };

    requestJson('/v1/billing/mode', { method: 'GET' })
      .then((payload) => {
        const mode = responseRecord(payload);
        if (typeof mode.billing_test_mode !== 'boolean') {
          throw new Error('billing mode response is malformed');
        }
        if (active) setBillingTestMode(mode.billing_test_mode);
      })
      .catch(() => {
        // An unavailable or unclassified mode never produces a guessed banner.
        if (active) setBillingTestMode(null);
      });

    return () => { active = false; };
  }, [session?.org_id]);

  if (billingTestMode === null) return null;
  return (
    <BillingModeBannerContent
      billingTestMode={billingTestMode}
      currentPath={location}
    />
  );
}

export default BillingModeBanner;
